// src/pages/onboarding/patient/steps/StepMedications.jsx
import React, { startTransition } from "react";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import Divider from "@/components/ui/Divider";
import Checkbox from "@/components/ui/Checkbox";
import TapButton from "@/pages/onboarding/_shared/TapButton.jsx";
import { Search as SearchIcon, Plus, CircleX as CircleXIcon } from "lucide-react";
import { loadMedications } from "@/utils/medications";
import { useAuth } from "@/contexts/AuthContext";
import supabase from "@/services/supabaseClient";

const COMPLETION_SAFE_MEDICATIONS = ["None", "Other"];

const Row = React.memo(function Row({ name, checked, onToggle }) {
  return (
    <div role="button" tabIndex={0}
         onClick={() => onToggle(name)}
         onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onToggle(name)}
         className="w-full flex items-center"
         style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}>
      <div className="flex-1 pl-5 pr-4 py-4 text-left">
        <span className="text-body-large text-[rgb(var(--clr-on-surface))]">{name}</span>
      </div>
      <div className="mr-4">
        <Checkbox checked={checked} onChange={(next) => { if (next !== checked) onToggle(name); }} />
      </div>
    </div>
  );
});

export default function StepMedications({ step, total, selected = [], onChange, onBack, onContinue }) {
  const { refreshProfile } = useAuth() || {};
  const [all, setAll] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [selSet, setSelSet] = React.useState(() => new Set(selected));
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  const CHUNK = 60;
  const [visible, setVisible] = React.useState(CHUNK);

  // ✅ search is deferred (fast typing)
  const deferredQuery = React.useDeferredValue(query);
  const q = deferredQuery.trim().toLowerCase();
  const options = React.useMemo(() => {
    const seen = new Set();
    const next = [];
    for (const option of [...COMPLETION_SAFE_MEDICATIONS, ...all]) {
      const canonical = String(option || "").trim();
      const key = canonical.toLowerCase();
      if (!canonical || seen.has(key)) continue;
      seen.add(key);
      next.push(canonical);
    }
    return next;
  }, [all]);

  // ✅ DEFINE filtered EARLY (before observer / shown)
  const filtered = React.useMemo(
    () => (!q ? options : options.filter((n) => n.toLowerCase().includes(q))),
    [options, q]
  );

  // ✅ reset chunk when query changes
  React.useEffect(() => {
    setVisible(CHUNK);
  }, [q]);

  // ✅ derive shown from filtered
  const shown = React.useMemo(() => {
    if (q) return filtered;               // searching = show all matches (no “missing items”)
    return filtered.slice(0, visible);    // browsing = chunk
  }, [filtered, q, visible]);

  // ✅ sentinel observer (uses filtered.length safely now)
  const sentRef = React.useRef(null);

  React.useEffect(() => {
    if (!sentRef.current) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible((v) => Math.min(v + CHUNK, filtered.length));
        }
      },
      { rootMargin: "400px" }
    );

    io.observe(sentRef.current);
    return () => io.disconnect();
  }, [filtered.length]);

  // ---- keep the rest of your file as-is ----
  const selectedKey = React.useMemo(
    () => JSON.stringify(Array.isArray(selected) ? selected.slice().sort() : []),
    [selected]
  );

  React.useEffect(() => {
    setSelSet(new Set(Array.isArray(selected) ? selected : []));
  }, [selectedKey]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const names = await loadMedications();
      if (!alive) return;
      setAll(names);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const emit = (setObj) => onChange?.(Array.from(setObj));

  const toggle = (name) => {
    setSelSet(prev => {
      const next = new Set(prev);
      const isSpecial = COMPLETION_SAFE_MEDICATIONS.includes(name);
      if (next.has(name)) {
        next.delete(name);
      } else if (isSpecial) {
        next.clear();
        next.add(name);
      } else {
        for (const option of COMPLETION_SAFE_MEDICATIONS) next.delete(option);
        next.add(name);
      }
      startTransition(() => emit(next));
      return next;
    });
  };

  const addCustom = () => {
    const raw = query.trim();
    if (!raw) return;
    const pretty = raw.replace(/\s+/g, " ").trim();
    if (!all.some(n => n.toLowerCase() === pretty.toLowerCase())) setAll(prev => [pretty, ...prev]);
    setSelSet(prev => {
      const next = new Set(prev);
      for (const option of COMPLETION_SAFE_MEDICATIONS) next.delete(option);
      next.add(pretty);
      startTransition(() => emit(next));
      return next;
    });
    setQuery("");
  };

  const addCustomRef = React.useRef(addCustom);
  React.useEffect(() => {
    addCustomRef.current = addCustom;
  }, [addCustom]);

  const handlePlusClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addCustomRef.current();
  };

  async function handleContinue() {
    if (selSet.size === 0 || saving) return;

    try {
      setSaving(true);
      setErr("");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) throw new Error("Session expired");

      const currentMedications = Array.from(selSet);
      const { error } = await supabase
        .from("patient_profiles")
        .upsert(
          {
            user_id: user.id,
            current_medications: currentMedications,
          },
          { onConflict: "user_id" }
        );

      if (error) throw error;

      await refreshProfile?.();
      onContinue?.();
    } catch (nextError) {
      setErr(nextError?.message || "Could not save medications");
    } finally {
      setSaving(false);
    }
  }

  const canContinue = selSet.size > 0;

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={
          <div className="space-y-3">
            <h2 className="text-title-large text-on-surface">Medications</h2>
            <div className="mt-3 flex items-center gap-3">
              <SearchIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search or add"
                inputMode="search"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                className="flex-1 bg-transparent outline-none border-0 focus:outline-none focus:ring-0
                           text-[16px] leading-6 placeholder:text-[rgb(var(--clr-on-surface-variant))]"
              />
              {!!query && (
                <TapButton ariaLabel="Clear search" onTap={() => setQuery("")} className="shrink-0 grid place-items-center">
                  <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]" />
                </TapButton>
              )}
              <button
                type="button"
                onMouseDown={handlePlusClick}
                onTouchStart={handlePlusClick}
                onClick={handlePlusClick}
                className="shrink-0 grid place-items-center rounded-full
                          w-8 h-8 border border-[rgba(24,50,119,0.16)]
                          hover:bg-[rgba(24,50,119,0.04)]
                          focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
                aria-label="Add medication"
              >
                <Plus className="h-4 w-4 text-[rgb(var(--clr-on-surface-variant))]" />
              </button>
            </div>
            <Divider variant="outline" full />
          </div>
        }
        footerTab footer={<Button size="lg" disabled={!canContinue || saving} onClick={handleContinue}><span className="text-title-medium">{saving ? "Saving..." : "Continue"}</span></Button>}
      >
        <div className="relative z-10 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-none overflow-hidden">
          {loading && (
            <div className="py-8 text-center text-body-medium text-on-surface-var">Loading… one moment please!</div>
          )}

          {!loading && shown.map((name, idx) => (
            <div key={`${name}-${idx}`}>
              <Row name={name} checked={selSet.has(name)} onToggle={toggle} />
              {idx < filtered.length - 1 && <Divider inset variant="outlineVariant" />}
            </div>
          ))}

          {!loading && filtered.length === 0 && (
            <div className="px-5 py-6 text-body-medium text-on-surface-var">
              No matches. Tap + to add “{query.trim()}”.
            </div>
          )}
        </div>
        {/* Load-more sentinel */}
        <div ref={sentRef} className="h-6" />
        {err && <p className="text-red-500 text-sm mt-3 text-center">{err}</p>}
      </OnboardingSection>
    </div>
  );
}
